import type { RendererPlugin, RenderContext } from '@/plugin'
import { RENDERER_PRIORITY } from '@/plugin'
import type { KLineData } from '@/types/price'
import { getKLineTrend, type kLineTrend } from '@/types/kLine'
import { createAlignedKLineFromPx, createVerticalLineRect } from '@/core/draw/pixelAlign'
import { PRICE_COLORS } from '@/core/theme/colors'
import { getPhysicalKLineConfig } from '@/core/chart'
import { VolumePriceRelation } from '@/types/volumePrice'
import { analyzeVolumePriceRelationBatch, DEFAULT_VOLUME_PRICE_CONFIG } from '@/utils/volumePrice'
import type { MarkerManager } from '@/core/marker/registry'

/**
 * 创建 K 线蜡烛图渲染器插件
 */
export function createCandleRenderer(): RendererPlugin {
    return {
        name: 'candle',
        version: '1.0.0',
        description: 'K线蜡烛图渲染器',
        debugName: 'K线',
        paneId: 'main',
        priority: RENDERER_PRIORITY.MAIN,

        draw(context: RenderContext) {
            const { ctx, pane, data, range, scrollLeft, kWidth, kGap, dpr, kLinePositions, markerManager } = context
            const klineData = data as KLineData[]
            if (!klineData.length) return

            const { kWidthPx } = getPhysicalKLineConfig(kWidth, kGap, dpr)

            ctx.save()
            ctx.translate(-scrollLeft, 0)
            const positions = kLinePositions || []

            // 批量计算量价关系，使用前缀和优化性能
            const relations = analyzeVolumePriceRelationBatch(
                klineData,
                range.start,
                range.end,
                DEFAULT_VOLUME_PRICE_CONFIG
            )

            for (let i = range.start; i < range.end && i < klineData.length; i++) {

                const e = klineData[i]
                if (!e) continue

                const openY = pane.yAxis.priceToY(e.open)
                const closeY = pane.yAxis.priceToY(e.close)
                const highY = pane.yAxis.priceToY(e.high)
                const lowY = pane.yAxis.priceToY(e.low)

                // 使用 Chart 统一计算的 x 坐标
                const leftLogical = positions[i - range.start]
                if (!leftLogical) continue

                // 对齐 Y 坐标到物理像素网格
                const alignY = (logical: number) => Math.round(logical * dpr) / dpr
                const alignedOpenY = alignY(openY)
                const alignedCloseY = alignY(closeY)
                const alignedHighY = alignY(highY)
                const alignedLowY = alignY(lowY)
                const alignedRawRectY = Math.min(alignedOpenY, alignedCloseY)
                const alignedRawRectH = Math.max(Math.abs(alignedOpenY - alignedCloseY), 1)

                const roundedLeftPx = Math.round(leftLogical * dpr)

                const aligned = createAlignedKLineFromPx(
                    roundedLeftPx,
                    alignedRawRectY,
                    kWidthPx,
                    alignedRawRectH,
                    dpr
                )

                const trend: kLineTrend = getKLineTrend(e)
                const color = trend === 'up' ? PRICE_COLORS.UP : PRICE_COLORS.DOWN

                ctx.fillStyle = color
                ctx.fillRect(aligned.bodyRect.x, aligned.bodyRect.y, aligned.bodyRect.width, aligned.bodyRect.height)

                const wickWidth = aligned.wickRect.width
                const wickX = aligned.wickRect.x
                const bodyTop = aligned.bodyRect.y
                const bodyBottom = aligned.bodyRect.y + aligned.bodyRect.height
                const bodyHigh = Math.max(e.open, e.close)
                const bodyLow = Math.min(e.open, e.close)

                if (e.high > bodyHigh) {
                    const wick = createVerticalLineRect(wickX, alignedHighY, bodyTop, dpr)
                    if (wick) ctx.fillRect(wick.x, wick.y, wickWidth, wick.height)
                }
                if (e.low < bodyLow) {
                    const wick = createVerticalLineRect(wickX, bodyBottom, alignedLowY, dpr)
                    if (wick) ctx.fillRect(wick.x, wick.y, wickWidth, wick.height)
                }

                // 绘制量价关系标记（小缩放下不显示，避免拥挤；可通过设置关闭）
                const relation = relations[i - range.start]
                const MIN_ZOOM_LEVEL_FOR_MARKER = 2
                const showVolumePriceMarkers = context.settings?.showVolumePriceMarkers ?? true
                if (showVolumePriceMarkers &&
                    relation !== VolumePriceRelation.OTHERS &&
                    markerManager &&
                    (context.zoomLevel ?? 1) >= MIN_ZOOM_LEVEL_FOR_MARKER) {
                    // 根据量价关系决定标记位置
                    const isRising = relation === VolumePriceRelation.RISE_WITH_VOLUME ||
                        relation === VolumePriceRelation.RISE_WITHOUT_VOLUME
                    const markerY = isRising ? alignedHighY - 15 : alignedLowY + 15
                    // 使用传下来的 kLineCenters（已物理像素对齐的影线中心）
                    const posIndex = i - range.start
                    const markerX = context.kLineCenters[posIndex]!

                    drawVolumePriceMarker(ctx, markerX, markerY, relation!, i, kWidth, 4, markerManager as MarkerManager, dpr)
                }

            }

            ctx.restore()
        },
    }
}

/**
* 绘制量价关系标记
* 在K线图上标注量价关系标记符号
*
* @param ctx - Canvas绘图上下文
* @param x - 标记的x坐标（三角形水平中心）
* @param y - 标记的y坐标（三角形底边/顶点与K线的接触点）
* @param relation - 量价关系类型
* @param kWidth - K线宽度，作为三角形边长
* @param gap - 三角形与K线的间距，默认为4
* @param dpr - 设备像素比
*/
export function drawVolumePriceMarker(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    relation: VolumePriceRelation,
    kIndex: number,
    kWidth: number,
    gap: number = 4,
    markerManager: MarkerManager,
    dpr: number
): void {
    // 对齐坐标到物理像素网格
    const align = (v: number) => Math.round(v * dpr) / dpr
    x = align(x)
    y = align(y)

    const sideLength = Math.min(kWidth, 20)
    // 等边三角形的高度 = 边长 * √3 / 2
    const height = sideLength * Math.sqrt(3) / 2

    let color: string
    let isUp: boolean

    switch (relation) {
        case VolumePriceRelation.RISE_WITH_VOLUME:
            // 量价齐升 - 红色向上箭头
            color = '#FF4444'
            isUp = true
            break
        case VolumePriceRelation.RISE_WITHOUT_VOLUME:
            // 缩量上涨 - 绿色向上箭头
            color = '#00C853'
            isUp = true
            break
        case VolumePriceRelation.FALL_WITH_VOLUME:
            // 放量下跌 - 红色向下箭头
            color = '#FF4444'
            isUp = false
            break
        case VolumePriceRelation.FALL_WITHOUT_VOLUME:
            // 缩量下跌 - 绿色向下箭头
            color = '#00C853'
            isUp = false
            break
        default:
            return
    }

    ctx.save()
    ctx.beginPath()

    if (isUp) {
        // 向上三角形：底边在下，顶点在上
        // y 是 highY，三角形底边距离 highY 有 gap 的间距
        const baseY = align(y - gap)           // 底边 y 坐标
        const tipY = align(baseY - height)     // 顶点 y 坐标

        ctx.moveTo(x, tipY)                                    // 顶点
        ctx.lineTo(align(x - sideLength / 2), baseY)           // 左下角
        ctx.lineTo(align(x + sideLength / 2), baseY)           // 右下角
    } else {
        // 向下三角形：底边在上，顶点在下
        // y 是 lowY，三角形底边距离 lowY 有 gap 的间距
        const baseY = align(y + gap)           // 底边 y 坐标
        const tipY = align(baseY + height)     // 顶点 y 坐标

        ctx.moveTo(x, tipY)                                    // 顶点
        ctx.lineTo(align(x - sideLength / 2), baseY)           // 左上角
        ctx.lineTo(align(x + sideLength / 2), baseY)           // 右上角
    }

    ctx.closePath()

    ctx.fillStyle = color
    ctx.fill()

    ctx.restore()

    // 计算三角形的包围盒坐标
    let boundingX: number
    let boundingY: number

    if (isUp) {
        // 向上三角形：底边在下，顶点在上
        const baseY = align(y - gap)           // 底边 y 坐标
        const tipY = align(baseY - height)     // 顶点 y 坐标
        boundingX = align(x - sideLength / 2)  // 左上角 x
        boundingY = tipY                        // 左上角 y（顶点 y）
    } else {
        // 向下三角形：底边在上，顶点在下
        const baseY = align(y + gap)           // 底边 y 坐标
        const tipY = align(baseY + height)     // 顶点 y 坐标
        boundingX = align(x - sideLength / 2)  // 左上角 x
        boundingY = baseY                       // 左上角 y（底边 y）
    }

    // 根据 VolumePriceRelation 获取对应的 markerType
    let markerTypeKey: string
    switch (relation) {
        case VolumePriceRelation.RISE_WITH_VOLUME:
            markerTypeKey = 'RISE_WITH_VOLUME'
            break
        case VolumePriceRelation.RISE_WITHOUT_VOLUME:
            markerTypeKey = 'RISE_WITHOUT_VOLUME'
            break
        case VolumePriceRelation.FALL_WITH_VOLUME:
            markerTypeKey = 'FALL_WITH_VOLUME'
            break
        case VolumePriceRelation.FALL_WITHOUT_VOLUME:
            markerTypeKey = 'FALL_WITHOUT_VOLUME'
            break
        default:
            return
    }

    const markerId = `mk_price-volume_${kIndex}`
    markerManager.register({
        id: markerId,
        type: 'triangle',
        markerType: markerTypeKey,
        x: boundingX,
        y: boundingY,
        width: sideLength,
        height: height,
        dataIndex: kIndex,
        metadata: { relation }
    })
}
